import type {
    SketchParametricEquation,
    SketchParametricEquationStrength,
    SketchParametricImplicitCurveDescriptor,
    SketchParametricImplicitCurveEvaluation,
    SketchParametricPathSegment,
    SketchParametricPointHandle,
    SketchParametricSplineCurveDescriptor,
    SketchParametricSplineDifferentialEvaluation,
    SketchParametricSplinePointEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricSplineEvaluator} from '../core/SketchParametricSplineEvaluator';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';

/**
 * 草图残差方程工厂。
 *
 * 职责：把尺寸/几何关系翻译为纯数据残差方程（语义蓝本：阶段 0 原型
 * `SketchPrototypeEquationFactory`，原型测试验证过的表达式原样迁移）。
 *
 * 约定：
 * 1. 每行残差显式声明量纲：长度使用米、角度使用弧度、方向与隐式值使用对应无量纲角色；
 * 2. 方向类表达式（平行/垂直/角度）统一按单位向量归一，保持雅可比量级一致；
 * 3. 零长度方向抛错（由分类器归为 invalid），不静默返回 0；
 * 4. 优化尺度、收敛容差和原始诊断统一由 `SketchParametricResidualEvaluator` 处理；
 * 5. 单线长度、平行线垂距分别复用两点距离、点线垂距方程（同一几何量），
 *    由编译器负责引用到点句柄的展开。
 */
export class SketchParametricEquationFactory {
    /** 方向退化判定阈值（米）。 */
    private static readonly DEGENERATE_LENGTH = SKETCH_PARAMETRIC_TOLERANCE.degenerateLength;

    /**
     * 两点绝对距离（尺寸：point-distance / length）。
     *
     * @param equationId 方程 ID。
     * @param first 第一点。
     * @param second 第二点。
     * @param targetDistance 目标距离（米）。
     * @returns 残差方程。
     */
    public static pointPointDistance(
        equationId: string,
        first: SketchParametricPointHandle,
        second: SketchParametricPointHandle,
        targetDistance: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-point-distance',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [first.xId, first.yId, second.xId, second.yId],
            branchSign: null,
            evaluate: (read) => {
                const dx = read(second.xId) - read(first.xId);
                const dy = read(second.yId) - read(first.yId);

                return [Math.hypot(dx, dy) - targetDistance];
            },
        };
    }

    /**
     * 两线段长度差锁定（尺寸：curve-offset-distance 的椭圆半轴差）。
     *
     * 语义：|firstEnd − firstStart| − |secondEnd − secondStart| = targetDifference
     * （如等距椭圆 |maj₂−c₂| − |maj₁−c₁| = ±d）。雅可比由数值差分提供。
     *
     * @param equationId 方程 ID。
     * @param firstStart 第一条线段起点（被减长度）。
     * @param firstEnd 第一条线段终点。
     * @param secondStart 第二条线段起点（减数长度）。
     * @param secondEnd 第二条线段终点。
     * @param targetDifference 目标长度差（带符号，米）。
     * @returns 残差方程。
     */
    public static distanceDifference(
        equationId: string,
        firstStart: SketchParametricPointHandle,
        firstEnd: SketchParametricPointHandle,
        secondStart: SketchParametricPointHandle,
        secondEnd: SketchParametricPointHandle,
        targetDifference: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'distance-difference',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                firstStart.xId, firstStart.yId, firstEnd.xId, firstEnd.yId,
                secondStart.xId, secondStart.yId, secondEnd.xId, secondEnd.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const firstLength = Math.hypot(
                    read(firstEnd.xId) - read(firstStart.xId),
                    read(firstEnd.yId) - read(firstStart.yId),
                );
                const secondLength = Math.hypot(
                    read(secondEnd.xId) - read(secondStart.xId),
                    read(secondEnd.yId) - read(secondStart.yId),
                );

                return [firstLength - secondLength - targetDifference];
            },
        };
    }

    /**
     * 抛物线对偏移距离锁定（尺寸：curve-offset-distance 的抛物线分支）。
     *
     * 等距抛物线的构造语义是「同轴、等焦距、顶点与焦点沿轴同步平移 d」，
     * 因此本方程直接把第二抛物线锁定为第一抛物线沿轴平移 branchSign·targetDistance
     * 的结果：
     * 1. 焦点—顶点向量相等（同轴、等焦距、同向，2 行）；
     * 2. 顶点差向量 = branchSign·targetDistance·axisDir₁（沿轴有向距离，2 行）。
     *
     * @param equationId 方程 ID。
     * @param firstFocal 第一抛物线焦点。
     * @param firstApex 第一抛物线顶点。
     * @param secondFocal 第二抛物线焦点。
     * @param secondApex 第二抛物线顶点。
     * @param targetDistance 目标顶点沿轴距离幅值（米）。
     * @param branchSign 方向分支：+1 表示第二顶点位于第一顶点朝向焦点的正轴上。
     * @returns 残差方程。
     */
    public static parabolaOffsetDistance(
        equationId: string,
        firstFocal: SketchParametricPointHandle,
        firstApex: SketchParametricPointHandle,
        secondFocal: SketchParametricPointHandle,
        secondApex: SketchParametricPointHandle,
        targetDistance: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'parabola-offset-distance',
            residualCount: 4,
            residualUnits: ['length', 'length', 'length', 'length'],
            parameterIds: [
                firstFocal.xId, firstFocal.yId, firstApex.xId, firstApex.yId,
                secondFocal.xId, secondFocal.yId, secondApex.xId, secondApex.yId,
            ],
            branchSign,
            evaluate: (read) => {
                // 第一抛物线轴向（顶点 → 焦点）；退化时抛错，由分类器归为 invalid
                const axisDir = SketchParametricEquationFactory.readUnitDirection(read, firstApex, firstFocal);
                // 焦点—顶点向量：锁定两抛物线同轴、等焦距、同向
                const firstVectorX = read(firstFocal.xId) - read(firstApex.xId);
                const firstVectorY = read(firstFocal.yId) - read(firstApex.yId);
                const secondVectorX = read(secondFocal.xId) - read(secondApex.xId);
                const secondVectorY = read(secondFocal.yId) - read(secondApex.yId);
                // 顶点差向量：锁定第二抛物线沿第一抛物线轴向平移 branchSign·d
                const apexDeltaX = read(secondApex.xId) - read(firstApex.xId);
                const apexDeltaY = read(secondApex.yId) - read(firstApex.yId);

                return [
                    secondVectorX - firstVectorX,
                    secondVectorY - firstVectorY,
                    apexDeltaX - branchSign * targetDistance * axisDir.x,
                    apexDeltaY - branchSign * targetDistance * axisDir.y,
                ];
            },
        };
    }

    /**
     * 两点水平距离（带方向分支）。
     *
     * @param equationId 方程 ID。
     * @param first 第一点。
     * @param second 第二点。
     * @param targetDistance 目标距离幅值（米）。
     * @param branchSign 方向分支：+1 表示第二点在 +x 侧。
     * @returns 残差方程。
     */
    public static pointPointHorizontalDistance(
        equationId: string,
        first: SketchParametricPointHandle,
        second: SketchParametricPointHandle,
        targetDistance: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-point-horizontal-distance',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [first.xId, second.xId],
            branchSign,
            evaluateJacobian: () => [[-1, 1]],
            evaluate: (read) => [
                (read(second.xId) - read(first.xId)) - branchSign * targetDistance,
            ],
        };
    }

    /**
     * 两点竖直距离（带方向分支）。
     *
     * @param equationId 方程 ID。
     * @param first 第一点。
     * @param second 第二点。
     * @param targetDistance 目标距离幅值（米）。
     * @param branchSign 方向分支：+1 表示第二点在 +y 侧。
     * @returns 残差方程。
     */
    public static pointPointVerticalDistance(
        equationId: string,
        first: SketchParametricPointHandle,
        second: SketchParametricPointHandle,
        targetDistance: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-point-vertical-distance',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [first.yId, second.yId],
            branchSign,
            evaluateJacobian: () => [[-1, 1]],
            evaluate: (read) => [
                (read(second.yId) - read(first.yId)) - branchSign * targetDistance,
            ],
        };
    }

    /**
     * 点到无限直线垂距（带侧分支；尺寸：point-line-distance / line-line-distance）。
     *
     * @param equationId 方程 ID。
     * @param point 目标点。
     * @param lineStart 直线上一点。
     * @param lineEnd 直线上另一点。
     * @param targetDistance 目标距离幅值（米）。
     * @param branchSign 侧分支：+1 表示 cross(线方向, 点-线起点) 为正的一侧。
     * @returns 残差方程。
     */
    public static pointLineDistance(
        equationId: string,
        point: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
        targetDistance: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-line-distance',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                point.xId, point.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
            ],
            branchSign,
            evaluate: (read) => {
                const direction = SketchParametricEquationFactory.readUnitDirection(read, lineStart, lineEnd);
                const px = read(point.xId) - read(lineStart.xId);
                const py = read(point.yId) - read(lineStart.yId);
                // 有符号垂距：单位方向与点向量的叉积。
                const signedDistance = direction.x * py - direction.y * px;

                return [signedDistance - branchSign * targetDistance];
            },
        };
    }

    /**
     * 两线夹角（带象限分支）。
     *
     * @param equationId 方程 ID。
     * @param firstStart 第一线起点。
     * @param firstEnd 第一线终点。
     * @param secondStart 第二线起点。
     * @param secondEnd 第二线终点。
     * @param targetAngleRad 目标角度幅值（弧度）。
     * @param branchSign 象限分支：+1 表示第二线方向相对第一线为逆时针。
     * @returns 残差方程。
     */
    public static lineLineAngle(
        equationId: string,
        firstStart: SketchParametricPointHandle,
        firstEnd: SketchParametricPointHandle,
        secondStart: SketchParametricPointHandle,
        secondEnd: SketchParametricPointHandle,
        targetAngleRad: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'line-line-angle',
            residualCount: 1,
            residualUnits: ['angle'],
            parameterIds: [
                firstStart.xId, firstStart.yId, firstEnd.xId, firstEnd.yId,
                secondStart.xId, secondStart.yId, secondEnd.xId, secondEnd.yId,
            ],
            branchSign,
            evaluate: (read) => {
                const first = SketchParametricEquationFactory.readUnitDirection(read, firstStart, firstEnd);
                const second = SketchParametricEquationFactory.readUnitDirection(read, secondStart, secondEnd);
                const cross = first.x * second.y - first.y * second.x;
                const dot = first.x * second.x + first.y * second.y;
                const signedAngle = Math.atan2(cross, dot);

                return [
                    SketchParametricEquationFactory.wrapToPi(signedAngle - branchSign * targetAngleRad),
                ];
            },
        };
    }

    /**
     * 两线等长（关系：Equal，线-线）。
     *
     * @param equationId 方程 ID。
     * @param firstStart 第一线起点。
     * @param firstEnd 第一线终点。
     * @param secondStart 第二线起点。
     * @param secondEnd 第二线终点。
     * @returns 残差方程。
     */
    public static equalLength(
        equationId: string,
        firstStart: SketchParametricPointHandle,
        firstEnd: SketchParametricPointHandle,
        secondStart: SketchParametricPointHandle,
        secondEnd: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'equal-length',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                firstStart.xId, firstStart.yId, firstEnd.xId, firstEnd.yId,
                secondStart.xId, secondStart.yId, secondEnd.xId, secondEnd.yId,
            ],
            branchSign: null,
            evaluate: (read) => [
                Math.hypot(read(firstEnd.xId) - read(firstStart.xId), read(firstEnd.yId) - read(firstStart.yId))
                - Math.hypot(read(secondEnd.xId) - read(secondStart.xId), read(secondEnd.yId) - read(secondStart.yId)),
            ],
        };
    }

    /**
     * 两条任意已参数化曲线的真实长度相等。
     *
     * @param equationId 方程 ID。
     * @param first 第一条曲线长度描述符。
     * @param second 第二条曲线长度描述符。
     * @returns 残差方程。
     */
    public static equalCurveLength(
        equationId: string,
        first: SketchParametricPathSegment,
        second: SketchParametricPathSegment,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'equal-curve-length',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                ...SketchParametricEquationFactory.resolvePathSegmentParameterIds(first),
                ...SketchParametricEquationFactory.resolvePathSegmentParameterIds(second),
            ],
            branchSign: null,
            evaluate: (read) => [
                SketchParametricEquationFactory.measurePathSegmentLength(read, first)
                - SketchParametricEquationFactory.measurePathSegmentLength(read, second),
            ],
        };
    }

    /**
     * 圆弧槽口结构耦合：从动点 = 弧心 + (R + offsetSign×h)·u(θ)。
     *
     * 语义：圆弧槽口的真实自由度是「弧心 + 中心弧半径 R + 半宽 h + 起止角」，
     * 4 个接合点、2 个端帽圆心都是它们的派生量。用极坐标残差把派生点钉住，
     * 求解器即可把圆弧槽口当整体驱动（拖任一成员/改任一尺寸都不散架）。
     *
     * **量纲纪律**：角度参数存的是等效弧长（θ×种子半径），残差里再折回弧度。
     * 若直接把弧度和米放进同一个最小范数度量，LM 会觉得"缩半径"比"转角度"便宜——
     * 实测表现为改角度尺寸时中心弧半径被一起拽塌。
     *
     * @param equationId 方程 ID。
     * @param center 弧心点句柄。
     * @param dependent 从动点句柄。
     * @param radiusParameterId 中心弧半径参数 ID。
     * @param halfWidthParameterId 半宽参数 ID。
     * @param angleParameterId 角度参数 ID（存的是**等效弧长** θ×angleScale，不做 2π 归一）。
     * @param offsetSign 法向偏移符号：+1 外弧、−1 内弧、0 中心弧（端帽圆心）。
     * @param angleScale 角度参数的长度尺度（种子半径）。
     * @returns 残差方程（x、y 各一条）。
     */
    public static arcSlotPolarBinding(
        equationId: string,
        center: SketchParametricPointHandle,
        dependent: SketchParametricPointHandle,
        radiusParameterId: string,
        halfWidthParameterId: string,
        angleParameterId: string,
        offsetSign: number,
        angleScale: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'arc-slot-polar-binding',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                center.xId, center.yId,
                dependent.xId, dependent.yId,
                radiusParameterId, halfWidthParameterId, angleParameterId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const angle = read(angleParameterId) / angleScale;
                const distance = read(radiusParameterId) + offsetSign * read(halfWidthParameterId);

                return [
                    read(dependent.xId) - (read(center.xId) + distance * Math.cos(angle)),
                    read(dependent.yId) - (read(center.yId) + distance * Math.sin(angle)),
                ];
            },
        };
    }

    /**
     * 圆弧槽口中心弧中点绑定：中点 = 弧心 + R·u((θs + θe) / 2)。
     *
     * 说明：起止角不做 2π 归一（采集侧保证 θe = θs + 有符号扫角），
     * 因此角平分恒落在弧内，跨 ±π 也不会取到反侧。
     *
     * @param equationId 方程 ID。
     * @param center 弧心点句柄。
     * @param midpoint 中心弧中点句柄。
     * @param radiusParameterId 中心弧半径参数 ID。
     * @param startAngleParameterId 起始角参数 ID（等效弧长）。
     * @param endAngleParameterId 终止角参数 ID（等效弧长）。
     * @param angleScale 角度参数的长度尺度（种子半径）。
     * @returns 残差方程（x、y 各一条）。
     */
    public static arcSlotMidpointBinding(
        equationId: string,
        center: SketchParametricPointHandle,
        midpoint: SketchParametricPointHandle,
        radiusParameterId: string,
        startAngleParameterId: string,
        endAngleParameterId: string,
        angleScale: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'arc-slot-midpoint-binding',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                center.xId, center.yId,
                midpoint.xId, midpoint.yId,
                radiusParameterId, startAngleParameterId, endAngleParameterId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const angle = (read(startAngleParameterId) + read(endAngleParameterId)) / (2 * angleScale);
                const radius = read(radiusParameterId);

                return [
                    read(midpoint.xId) - (read(center.xId) + radius * Math.cos(angle)),
                    read(midpoint.yId) - (read(center.yId) + radius * Math.sin(angle)),
                ];
            },
        };
    }

    /**
     * 标量派生：目标 = 基准 + 符号 × 偏置（如外弧半径 = 中心弧半径 + 半宽）。
     *
     * @param equationId 方程 ID。
     * @param targetParameterId 目标参数 ID。
     * @param baseParameterId 基准参数 ID。
     * @param offsetParameterId 偏置参数 ID。
     * @param offsetSign 偏置符号。
     * @returns 残差方程。
     */
    public static scalarOffsetEqual(
        equationId: string,
        targetParameterId: string,
        baseParameterId: string,
        offsetParameterId: string,
        offsetSign: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'scalar-offset-equal',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [targetParameterId, baseParameterId, offsetParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1, -1, -offsetSign]],
            evaluate: (read) => [
                read(targetParameterId) - (read(baseParameterId) + offsetSign * read(offsetParameterId)),
            ],
        };
    }

    /**
     * 标量跨度等于定值：(a2 − a1) = target。
     *
     * 用于圆弧槽口的跨角尺寸：起止角参数不做 2π 归一，二者相减就是有符号扫角，
     * 优角（>180°）与劣角天然可分，不像两射线夹角那样在 wrapToPi 下二义。
     *
     * @param equationId 方程 ID。
     * @param endParameterId 跨度终点参数 ID。
     * @param startParameterId 跨度起点参数 ID。
     * @param target 目标跨度（含符号）。
     * @returns 残差方程。
     */
    public static scalarSpanTarget(
        equationId: string,
        endParameterId: string,
        startParameterId: string,
        target: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'scalar-span-target',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [endParameterId, startParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1, -1]],
            evaluate: (read) => [
                (read(endParameterId) - read(startParameterId)) - target,
            ],
        };
    }

    /**
     * 两标量跨度相等：(a2 − a1) = (b2 − b1)。
     *
     * 用于「相等槽口」的圆弧槽口分支——半径已相等时，弧长相等等价于扫角相等。
     *
     * @param equationId 方程 ID。
     * @param firstStartId 第一跨度起点参数 ID。
     * @param firstEndId 第一跨度终点参数 ID。
     * @param secondStartId 第二跨度起点参数 ID。
     * @param secondEndId 第二跨度终点参数 ID。
     * @returns 残差方程。
     */
    public static scalarSpanEqual(
        equationId: string,
        firstStartId: string,
        firstEndId: string,
        secondStartId: string,
        secondEndId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'scalar-span-equal',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [firstStartId, firstEndId, secondStartId, secondEndId],
            branchSign: null,
            evaluateJacobian: () => [[-1, 1, 1, -1]],
            evaluate: (read) => [
                (read(firstEndId) - read(firstStartId)) - (read(secondEndId) - read(secondStartId)),
            ],
        };
    }

    /**
     * 两标量参数相等（结构耦合，如槽口端帽半径 = 槽口半宽）。
     *
     * @param equationId 方程 ID。
     * @param firstParameterId 第一参数 ID。
     * @param secondParameterId 第二参数 ID。
     * @returns 残差方程。
     */
    public static scalarEqual(
        equationId: string,
        firstParameterId: string,
        secondParameterId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'scalar-equal',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [firstParameterId, secondParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1, -1]],
            evaluate: (read) => [read(firstParameterId) - read(secondParameterId)],
        };
    }

    /**
     * 两标量参数差锁定（尺寸：curve-offset-distance 的圆/弧半径差）。
     *
     * 语义：first − second = targetDifference（如等距圆 r₂ − r₁ = ±d）。
     *
     * @param equationId 方程 ID。
     * @param firstParameterId 第一参数 ID（被减数）。
     * @param secondParameterId 第二参数 ID（减数）。
     * @param targetDifference 目标差值（带符号，米）。
     * @returns 残差方程。
     */
    public static scalarDifference(
        equationId: string,
        firstParameterId: string,
        secondParameterId: string,
        targetDifference: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'scalar-difference',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [firstParameterId, secondParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1, -1]],
            evaluate: (read) => [
                read(firstParameterId) - read(secondParameterId) - targetDifference,
            ],
        };
    }

    /**
     * 两圆系等半径（关系：Equal，圆/弧-圆/弧）。
     *
     * @param equationId 方程 ID。
     * @param firstRadiusParameterId 第一半径参数 ID。
     * @param secondRadiusParameterId 第二半径参数 ID。
     * @returns 残差方程。
     */
    public static equalRadius(
        equationId: string,
        firstRadiusParameterId: string,
        secondRadiusParameterId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'equal-radius',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [firstRadiusParameterId, secondRadiusParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1, -1]],
            evaluate: (read) => [read(firstRadiusParameterId) - read(secondRadiusParameterId)],
        };
    }

    /**
     * 点位于线段中点（关系：Midpoint）。
     *
     * @param equationId 方程 ID。
     * @param point 目标点。
     * @param lineStart 线起点。
     * @param lineEnd 线终点。
     * @returns 残差方程（x、y 各一条）。
     */
    public static midpoint(
        equationId: string,
        point: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'midpoint',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                point.xId, point.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
            ],
            branchSign: null,
            evaluateJacobian: () => [
                [1, 0, -0.5, 0, -0.5, 0],
                [0, 1, 0, -0.5, 0, -0.5],
            ],
            evaluate: (read) => [
                read(point.xId) - (read(lineStart.xId) + read(lineEnd.xId)) / 2,
                read(point.yId) - (read(lineStart.yId) + read(lineEnd.yId)) / 2,
            ],
        };
    }

    /**
     * 两点关于无限轴线对称（关系：Symmetric）。
     *
     * 两条二维残差完整表达轴对称：
     * 1. 两点在轴向上的投影相等；
     * 2. 两点相对轴线的有符号法向距离互为相反数。
     * @param equationId 方程 ID。
     * @param axisStart 镜向轴起点。
     * @param axisEnd 镜向轴终点。
     * @param sourcePoint 源点。
     * @param mirroredPoint 镜向点。
     * @returns 两条长度残差方程。
     */
    public static symmetricPoints(
        equationId: string,
        axisStart: SketchParametricPointHandle,
        axisEnd: SketchParametricPointHandle,
        sourcePoint: SketchParametricPointHandle,
        mirroredPoint: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'symmetric-points',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                axisStart.xId, axisStart.yId,
                axisEnd.xId, axisEnd.yId,
                sourcePoint.xId, sourcePoint.yId,
                mirroredPoint.xId, mirroredPoint.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const directionX = read(axisEnd.xId) - read(axisStart.xId);
                const directionY = read(axisEnd.yId) - read(axisStart.yId);
                const sourceX = read(sourcePoint.xId) - read(axisStart.xId);
                const sourceY = read(sourcePoint.yId) - read(axisStart.yId);
                const mirroredX = read(mirroredPoint.xId) - read(axisStart.xId);
                const mirroredY = read(mirroredPoint.yId) - read(axisStart.yId);
                const directionLengthSquared = directionX * directionX + directionY * directionY;

                if (directionLengthSquared
                    < SketchParametricEquationFactory.DEGENERATE_LENGTH ** 2) {
                    throw new Error(`镜向轴退化：${equationId}`);
                }
                const projectionScale = 2 * (
                    sourceX * directionX + sourceY * directionY
                ) / directionLengthSquared;

                /**
                 * 直接使用反射矩阵 `2ddᵀ/(dᵀd)-I`。它对 d 与 -d 给出完全相同的
                 * 残差，因此轴端点身份反序时求解目标不会突然变号；同时又不能
                 * 通过缩短轴长来人为缩小对称残差。仅严格零长度仍是未定义几何。
                 */
                return [
                    mirroredX - (projectionScale * directionX - sourceX),
                    mirroredY - (projectionScale * directionY - sourceY),
                ];
            },
        };
    }

    /**
     * 点位于无限直线上（关系：Coincident 的点-线形态 / Collinear 的位置分量）。
     *
     * @param equationId 方程 ID。
     * @param point 目标点。
     * @param lineStart 线上一点。
     * @param lineEnd 线上另一点。
     * @returns 残差方程。
     */
    public static pointOnLine(
        equationId: string,
        point: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-on-line',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                point.xId, point.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const direction = SketchParametricEquationFactory.readUnitDirection(read, lineStart, lineEnd);
                const px = read(point.xId) - read(lineStart.xId);
                const py = read(point.yId) - read(lineStart.yId);

                // 有符号垂距为 0 即点在线上。
                return [direction.x * py - direction.y * px];
            },
        };
    }

    /**
     * 校验一条直线的两个端点没有退化为同一点。
     *
     * 该方程只承担几何合法性闸门，不增加等式约束或改变直线自由度。求解候选进入
     * 零长度容差时抛出异常，由统一参数求解入口归类为 Invalid Solution 并拒绝写回。
     *
     * @param equationId 方程 ID。
     * @param start 直线起点。
     * @param end 直线终点。
     * @returns 零残差的非退化校验方程。
     */
    public static nondegenerateLineGuard(
        equationId: string,
        start: SketchParametricPointHandle,
        end: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'line-nondegenerate-guard',
            residualCount: 1,
            residualUnits: ['dimensionless'],
            parameterIds: [start.xId, start.yId, end.xId, end.yId],
            branchSign: null,
            evaluateJacobian: () => [[0, 0, 0, 0]],
            evaluate: (read) => {
                const dx = read(end.xId) - read(start.xId);
                const dy = read(end.yId) - read(start.yId);

                if (Math.hypot(dx, dy) <= SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`直线端点退化为同一点：${equationId}`);
                }

                return [0];
            },
        };
    }

    /**
     * 样条结构耦合：控制点 = 锚点 + 缩放系数 × 初始偏移。
     *
     * 语义：样条的采样曲线由 Worker 内的 Puppy CAD 内核插值算出，残差里无法同步复算，
     * 因此把样条的可驱动自由度显式收敛为「整体平移（锚点）+ 整体缩放（s）」——
     * 插值样条对相似变换等变，缩放 s 倍则弧长恰好 s 倍，这是精确关系而非近似。
     * 形状本身在本次求解内保持不变（不猜测 SW 的局部变形语义）。
     *
     * @param equationId 方程 ID。
     * @param anchor 锚点句柄（样条首控制点，自由平移）。
     * @param dependent 从动控制点句柄。
     * @param scaleParameterId 缩放系数参数 ID（seed=1）。
     * @param offsetX 从动点相对锚点的初始偏移 X。
     * @param offsetY 从动点相对锚点的初始偏移 Y。
     * @returns 残差方程（x、y 各一条）。
     */
    public static splineScaleBinding(
        equationId: string,
        anchor: SketchParametricPointHandle,
        dependent: SketchParametricPointHandle,
        scaleParameterId: string,
        offsetX: number,
        offsetY: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'spline-scale-binding',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                anchor.xId, anchor.yId,
                dependent.xId, dependent.yId,
                scaleParameterId,
            ],
            branchSign: null,
            evaluateJacobian: () => [
                [-1, 0, 1, 0, -offsetX],
                [0, -1, 0, 1, -offsetY],
            ],
            evaluate: (read) => {
                const scale = read(scaleParameterId);

                return [
                    read(dependent.xId) - (read(anchor.xId) + scale * offsetX),
                    read(dependent.yId) - (read(anchor.yId) + scale * offsetY),
                ];
            },
        };
    }

    /**
     * 样条总长（尺寸：spline-length）。
     *
     * 语义：总长 = 初始采样弧长 × 缩放系数（弧长对相似变换一次齐次）。
     *
     * @param equationId 方程 ID。
     * @param scaleParameterId 缩放系数参数 ID。
     * @param baseLength 初始采样弧长（米）。
     * @param targetLength 目标总长（米）。
     * @returns 残差方程。
     */
    public static splineLength(
        equationId: string,
        scaleParameterId: string,
        baseLength: number,
        targetLength: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'spline-length',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [scaleParameterId],
            branchSign: null,
            evaluateJacobian: () => [[baseLength]],
            evaluate: (read) => [baseLength * read(scaleParameterId) - targetLength],
        };
    }

    /**
     * 点位于圆周（圆弧结构：端点/中点到圆心的距离 = 半径）。
     *
     * 语义：圆弧的真实自由度是「圆心 + 半径 + 两端角」，端点是派生量。求解器只写点位、
     * 而 `SketchArcObject.refreshGeometryOnly` 会把端点**吸附**到精确圆周上——若解出的
     * 端点不在同一圆上，这次吸附就会把端点挪走，与之共享该端点的线段随之被拉短
     * （表现为"尺寸标 80mm、实得 76mm"的静默错值）。把该结构关系声明成方程，
     * 解本身就是自洽的，吸附成为恒等操作。
     *
     * @param equationId 方程 ID。
     * @param point 圆周上的点。
     * @param center 圆心点。
     * @param radiusParameterId 半径参数 ID。
     * @returns 残差方程。
     */
    public static pointOnCircle(
        equationId: string,
        point: SketchParametricPointHandle,
        center: SketchParametricPointHandle,
        radiusParameterId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-on-circle',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [point.xId, point.yId, center.xId, center.yId, radiusParameterId],
            branchSign: null,
            evaluateJacobian: (read) => {
                const dx = read(point.xId) - read(center.xId);
                const dy = read(point.yId) - read(center.yId);
                const distance = Math.hypot(dx, dy);

                if (distance <= SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`圆周点方向退化：${equationId}`);
                }
                return [[dx / distance, dy / distance, -dx / distance, -dy / distance, -1]];
            },
            evaluate: (read) => {
                const dx = read(point.xId) - read(center.xId);
                const dy = read(point.yId) - read(center.yId);

                return [Math.hypot(dx, dy) - read(radiusParameterId)];
            },
        };
    }

    /**
     * 线与圆/圆弧相切（关系：Tangent）。
     *
     * 语义：圆心到无限直线的有符号垂距 = 分支符号 × 半径。半径是参数而非常数，
     * 因此相切会把直线与曲线的自由度真正耦合起来——线动则圆心/半径跟随，
     * 这正是切线弧在 SOLIDWORKS 里的行为。
     *
     * @param equationId 方程 ID。
     * @param center 圆心点。
     * @param lineStart 直线上一点。
     * @param lineEnd 直线上另一点。
     * @param radiusParameterId 半径参数 ID。
     * @param branchSign 切于哪一侧：+1 表示圆心位于 cross(线方向, 点向量) 为正的一侧。
     * @returns 残差方程。
     */
    public static lineCurveTangent(
        equationId: string,
        center: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
        radiusParameterId: string,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'line-curve-tangent',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                center.xId, center.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
                radiusParameterId,
            ],
            branchSign,
            evaluate: (read) => {
                const direction = SketchParametricEquationFactory.readUnitDirection(read, lineStart, lineEnd);
                const px = read(center.xId) - read(lineStart.xId);
                const py = read(center.yId) - read(lineStart.yId);
                const signedDistance = direction.x * py - direction.y * px;

                return [signedDistance - branchSign * read(radiusParameterId)];
            },
        };
    }

    /**
     * 两圆弧相切（关系：Tangent，切线弧从圆弧端点起步）。
     *
     * 语义：两圆心的距离 = 半径的代数和（外切为 r₁ + r₂，内切为 |r₁ − r₂|）。
     * 说明：
     * 1. 共享端点已由两圆弧各自的 `point-on-circle` 方程钉在两圆上，
     *    两圆相切时交点唯一，因此共享端点必为切点，无需再显式声明三点共线；
     * 2. 内外切分支由关系持久提示锁定；首次无提示时才由编译期几何判定符号
     *    （与 `lineCurveTangent` 的 branchSign 同范式）；
     * 3. 内切分支要求大半径减小半径为正，编译期按当前半径大小决定符号组合。
     *
     * @param equationId 方程 ID。
     * @param centerA 圆弧 A 圆心点。
     * @param centerB 圆弧 B 圆心点。
     * @param radiusAParameterId 圆弧 A 半径参数 ID。
     * @param radiusBParameterId 圆弧 B 半径参数 ID。
     * @param signA 半径 A 的代数符号：外切为 +1，内切且 A 为大圆时为 +1，否则 −1。
     * @param signB 半径 B 的代数符号：外切为 +1，内切且 B 为大圆时为 +1，否则 −1。
     * @returns 残差方程。
     */
    public static arcArcTangent(
        equationId: string,
        centerA: SketchParametricPointHandle,
        centerB: SketchParametricPointHandle,
        radiusAParameterId: string,
        radiusBParameterId: string,
        signA: 1 | -1,
        signB: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'arc-arc-tangent',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                centerA.xId, centerA.yId,
                centerB.xId, centerB.yId,
                radiusAParameterId,
                radiusBParameterId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const dx = read(centerB.xId) - read(centerA.xId);
                const dy = read(centerB.yId) - read(centerA.yId);
                const distance = Math.hypot(dx, dy);
                const radiusSum = signA * read(radiusAParameterId) + signB * read(radiusBParameterId);

                return [distance - radiusSum];
            },
        };
    }

    /**
     * 圆/圆弧半径（尺寸：radius；diameter 由编译器按 valueScale 换算为半径目标值）。
     *
     * @param equationId 方程 ID。
     * @param radiusParameterId 圆半径参数 ID。
     * @param targetRadius 目标半径（米）。
     * @returns 残差方程。
     */
    public static circleRadius(
        equationId: string,
        radiusParameterId: string,
        targetRadius: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'circle-radius',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [radiusParameterId],
            branchSign: null,
            evaluateJacobian: () => [[1]],
            evaluate: (read) => [read(radiusParameterId) - targetRadius],
        };
    }

    /**
     * 创建圆系实体半径到固定属性输入值的瞬态严格偏好。
     *
     * 求解器会先把实体半径复制为固定目标值并临时冻结该参数，再在全部正式关系的
     * 可行域内移动其它自由度；若目标不可达，则恢复硬约束可行解而不破坏正式关系。
     *
     * @param equationId 方程 ID。
     * @param radiusParameterId 被编辑圆系实体的半径参数 ID。
     * @param fixedTargetParameterId 保存用户输入值的固定半径参数 ID。
     * @returns 带严格 seed 绑定的半径偏好方程。
     */
    public static entityRadiusTargetPreference(
        equationId: string,
        radiusParameterId: string,
        fixedTargetParameterId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'operation-entity-radius-target',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [radiusParameterId, fixedTargetParameterId],
            branchSign: null,
            strength: 'preference',
            preferenceSeedBindings: [{
                targetParameterId: radiusParameterId,
                sourceParameterId: fixedTargetParameterId,
            }],
            evaluateJacobian: () => [[1, -1]],
            evaluate: (read) => [read(radiusParameterId) - read(fixedTargetParameterId)],
        };
    }

    /**
     * 直槽口结构耦合：把从动点绑定到「轴两端 + 半宽」。
     *
     * 语义：轴起点 A、终点 B、单位方向 u=normalize(B−A)、法向 n=(−u.y, u.x)、半宽 h，
     * 从动点目标位置 = `A + (B−A)·alongRatio + n·h·offsetSign`。据此可表达槽口全部从动点：
     * - p1 = A + n·h（alongRatio=0、offsetSign=+1）、p2 = B + n·h（1、+1）
     * - p3 = B − n·h（1、−1）、p4 = A − n·h（0、−1）
     * - 端帽圆心 = A / B（offsetSign=0）——端帽圆心与轴端点是不同对象，必须显式绑定。
     *
     * 这样槽口的真实自由度收敛为 5（轴 4 + 半宽 1）：从动点虽仍是自由参数，
     * 但被本方程钉在结构位置上，求解器因此把槽口当整体处理，不会只挪被标注那条边。
     *
     * @param equationId 方程 ID。
     * @param axisStart 轴起点句柄。
     * @param axisEnd 轴终点句柄。
     * @param halfWidthParameterId 半宽参数 ID。
     * @param dependent 从动点句柄。
     * @param alongRatio 沿轴插值比（0=轴起点、1=轴终点）。
     * @param offsetSign 法向偏移符号（+1/−1/0）。
     * @returns 残差方程（x、y 各一条）。
     */
    public static slotStructureBinding(
        equationId: string,
        axisStart: SketchParametricPointHandle,
        axisEnd: SketchParametricPointHandle,
        halfWidthParameterId: string,
        dependent: SketchParametricPointHandle,
        alongRatio: number,
        offsetSign: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'slot-structure-binding',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                axisStart.xId, axisStart.yId,
                axisEnd.xId, axisEnd.yId,
                halfWidthParameterId,
                dependent.xId, dependent.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const ax = read(axisStart.xId);
                const ay = read(axisStart.yId);
                const bx = read(axisEnd.xId);
                const by = read(axisEnd.yId);
                const halfWidth = read(halfWidthParameterId);
                const dx = bx - ax;
                const dy = by - ay;
                const axisLength = Math.hypot(dx, dy);

                // 轴退化：法向无定义，退化为"从动点贴合轴插值点"，避免除零产生 NaN 污染整个雅可比。
                if (axisLength < SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
                    return [
                        read(dependent.xId) - (ax + dx * alongRatio),
                        read(dependent.yId) - (ay + dy * alongRatio),
                    ];
                }

                const normalX = -dy / axisLength;
                const normalY = dx / axisLength;
                const targetX = ax + dx * alongRatio + normalX * halfWidth * offsetSign;
                const targetY = ay + dy * alongRatio + normalY * halfWidth * offsetSign;

                return [read(dependent.xId) - targetX, read(dependent.yId) - targetY];
            },
        };
    }

    /**
     * 水平关系：两点 y 相等。
     *
     * @param equationId 方程 ID。
     * @param start 线起点。
     * @param end 线终点。
     * @returns 残差方程。
     */
    public static horizontal(
        equationId: string,
        start: SketchParametricPointHandle,
        end: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'relation-horizontal',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [start.yId, end.yId],
            branchSign: null,
            evaluateJacobian: () => [[-1, 1]],
            evaluate: (read) => [read(end.yId) - read(start.yId)],
        };
    }

    /**
     * 竖直关系：两点 x 相等。
     *
     * @param equationId 方程 ID。
     * @param start 线起点。
     * @param end 线终点。
     * @returns 残差方程。
     */
    public static vertical(
        equationId: string,
        start: SketchParametricPointHandle,
        end: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'relation-vertical',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [start.xId, end.xId],
            branchSign: null,
            evaluateJacobian: () => [[-1, 1]],
            evaluate: (read) => [read(end.xId) - read(start.xId)],
        };
    }

    /**
     * 重合关系：两点坐标相等（两行残差）。
     *
     * @param equationId 方程 ID。
     * @param first 第一点。
     * @param second 第二点。
     * @param strength 方程层级；普通关系为硬约束，操作目标为偏好。
     * @returns 残差方程。
     */
    public static coincident(
        equationId: string,
        first: SketchParametricPointHandle,
        second: SketchParametricPointHandle,
        strength: SketchParametricEquationStrength = 'hard',
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'relation-coincident',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [first.xId, first.yId, second.xId, second.yId],
            branchSign: null,
            strength,
            evaluateJacobian: () => [
                [-1, 0, 1, 0],
                [0, -1, 0, 1],
            ],
            evaluate: (read) => [
                read(second.xId) - read(first.xId),
                read(second.yId) - read(first.yId),
            ],
        };
    }

    /**
     * 两个同量纲坐标参数相等。
     *
     * 点重合关系通常应使用 {@link coincident} 同时声明二维事实；本公式用于拖拽
     * 稳定层按坐标分量检查可行性，避免某一方向受正式关系驱动时连带丢失另一
     * 方向仍可满足的稳定目标。
     *
     * @param equationId 方程 ID。
     * @param firstParameterId 第一坐标参数 ID。
     * @param secondParameterId 第二坐标参数 ID。
     * @param strength 方程层级。
     * @returns 单坐标长度残差方程。
     */
    public static coordinateCoincident(
        equationId: string,
        firstParameterId: string,
        secondParameterId: string,
        strength: SketchParametricEquationStrength = 'hard',
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'coordinate-coincident',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [firstParameterId, secondParameterId],
            branchSign: null,
            strength,
            evaluateJacobian: () => [[-1, 1]],
            evaluate: (read) => [read(secondParameterId) - read(firstParameterId)],
        };
    }

    /**
     * 平行关系：按种子方向分支令两条单位方向向量相等或相反。
     *
     * 不能只使用单位方向叉积：当种子恰好垂直时，`sin(θ)` 位于极值点，
     * 数值 Jacobian 为零，LM 无法离开该奇异起点。两个方向分量方程在目标解处
     * 仍只有一个独立方向自由度，但在垂直种子处保留有效的一阶导数。
     *
     * @param equationId 方程 ID。
     * @param firstStart 第一线起点。
     * @param firstEnd 第一线终点。
     * @param secondStart 第二线起点。
     * @param secondEnd 第二线终点。
     * @returns 残差方程。
     */
    public static parallel(
        equationId: string,
        firstStart: SketchParametricPointHandle,
        firstEnd: SketchParametricPointHandle,
        secondStart: SketchParametricPointHandle,
        secondEnd: SketchParametricPointHandle,
    ): SketchParametricEquation {
        let directionBranchSign: 1 | -1 | null = null;

        return {
            equationId,
            kind: 'relation-parallel',
            residualCount: 2,
            residualUnits: ['direction', 'direction'],
            parameterIds: [
                firstStart.xId, firstStart.yId, firstEnd.xId, firstEnd.yId,
                secondStart.xId, secondStart.yId, secondEnd.xId, secondEnd.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const first = SketchParametricEquationFactory.readUnitDirection(read, firstStart, firstEnd);
                const second = SketchParametricEquationFactory.readUnitDirection(read, secondStart, secondEnd);

                // 首次评价即为本轮 seed；锁定同向/反向分支，避免迭代中端点语义翻转。
                directionBranchSign ??= first.x * second.x + first.y * second.y < 0 ? -1 : 1;

                return [
                    second.x - directionBranchSign * first.x,
                    second.y - directionBranchSign * first.y,
                ];
            },
        };
    }

    /**
     * 垂直关系：按种子侧分支令第二单位方向等于第一方向旋转 ±90°。
     *
     * 与平行关系相同，单独使用点积会在两线恰好平行时产生零 Jacobian；
     * 分量方程保持明确旋转侧，并让奇异种子仍具有可用的一阶下降方向。
     *
     * @param equationId 方程 ID。
     * @param firstStart 第一线起点。
     * @param firstEnd 第一线终点。
     * @param secondStart 第二线起点。
     * @param secondEnd 第二线终点。
     * @returns 残差方程。
     */
    public static perpendicular(
        equationId: string,
        firstStart: SketchParametricPointHandle,
        firstEnd: SketchParametricPointHandle,
        secondStart: SketchParametricPointHandle,
        secondEnd: SketchParametricPointHandle,
    ): SketchParametricEquation {
        let rotationBranchSign: 1 | -1 | null = null;

        return {
            equationId,
            kind: 'relation-perpendicular',
            residualCount: 2,
            residualUnits: ['direction', 'direction'],
            parameterIds: [
                firstStart.xId, firstStart.yId, firstEnd.xId, firstEnd.yId,
                secondStart.xId, secondStart.yId, secondEnd.xId, secondEnd.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const first = SketchParametricEquationFactory.readUnitDirection(read, firstStart, firstEnd);
                const second = SketchParametricEquationFactory.readUnitDirection(read, secondStart, secondEnd);

                // 叉积符号区分逆时针/顺时针 90°；零值按逆时针稳定落种。
                rotationBranchSign ??= first.x * second.y - first.y * second.x < 0 ? -1 : 1;
                const rotatedX = -rotationBranchSign * first.y;
                const rotatedY = rotationBranchSign * first.x;

                return [second.x - rotatedX, second.y - rotatedY];
            },
        };
    }

    /**
     * 带圆系半径偏置的两点距离（Center/Min/Max 附着，ARC-001）。
     *
     * 语义：测量值 = 锚点距离 + Σ(sign × r)。Min（近侧圆周）该圆 sign=-1
     * （测量值 = 中心距 - r），Max（远侧圆周）sign=+1。
     *
     * @param equationId 方程 ID。
     * @param first 第一锚点（圆系为圆心）。
     * @param second 第二锚点。
     * @param radiusOffsets 半径偏置（半径参数 ID + 符号）。
     * @param targetDistance 目标测量值（米）。
     * @returns 残差方程。
     */
    public static pointPointDistanceWithRadiusOffsets(
        equationId: string,
        first: SketchParametricPointHandle,
        second: SketchParametricPointHandle,
        radiusOffsets: readonly {readonly radiusParameterId: string; readonly sign: 1 | -1}[],
        targetDistance: number,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-point-distance-arc-condition',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                first.xId, first.yId, second.xId, second.yId,
                ...radiusOffsets.map((offset) => offset.radiusParameterId),
            ],
            branchSign: null,
            evaluate: (read) => {
                const dx = read(second.xId) - read(first.xId);
                const dy = read(second.yId) - read(first.yId);
                const offset = radiusOffsets
                    .reduce((sum, item) => sum + item.sign * read(item.radiusParameterId), 0);

                return [Math.hypot(dx, dy) + offset - targetDistance];
            },
        };
    }

    /**
     * 带圆系半径偏置的点到无限直线垂距（圆-线 Min/Max 附着）。
     *
     * @param equationId 方程 ID。
     * @param point 目标点（圆系为圆心）。
     * @param lineStart 直线上一点。
     * @param lineEnd 直线上另一点。
     * @param radiusOffsets 半径偏置（半径参数 ID + 符号；Min=-1、Max=+1）。
     * @param targetDistance 目标测量值幅值（米）。
     * @param branchSign 侧分支。
     * @returns 残差方程。
     */
    public static pointLineDistanceWithRadiusOffsets(
        equationId: string,
        point: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
        radiusOffsets: readonly {readonly radiusParameterId: string; readonly sign: 1 | -1}[],
        targetDistance: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-line-distance-arc-condition',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                point.xId, point.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
                ...radiusOffsets.map((offset) => offset.radiusParameterId),
            ],
            branchSign,
            evaluate: (read) => {
                const direction = SketchParametricEquationFactory.readUnitDirection(read, lineStart, lineEnd);
                const px = read(point.xId) - read(lineStart.xId);
                const py = read(point.yId) - read(lineStart.yId);
                const signedDistance = direction.x * py - direction.y * px;
                const offset = radiusOffsets
                    .reduce((sum, item) => sum + item.sign * read(item.radiusParameterId), 0);

                // 测量值 = |垂距| + Σ(sign×r)：按侧分支展开为有符号形式。
                return [signedDistance - branchSign * (targetDistance - offset)];
            },
        };
    }

    /**
     * 真弧长（SD-SEL-008）：弧长 = 平均半径 × 扫角。
     *
     * 说明：
     * 1. 半径取两端点到圆心距离的均值（不依赖 radius 参数与端点几何的一致性）；
     * 2. 扫角按弧走向（branchSign=+1 逆时针 / -1 顺时针）从起点方向角到终点方向角
     *    规约到 (0, 2π)；
     * 3. 端点贴住圆心时抛错（invalid 语义来源）。
     *
     * @param equationId 方程 ID。
     * @param center 圆心点。
     * @param start 弧起点。
     * @param end 弧终点。
     * @param targetLength 目标弧长（米）。
     * @param branchSign 弧走向分支。
     * @returns 残差方程。
     */
    public static arcLength(
        equationId: string,
        center: SketchParametricPointHandle,
        start: SketchParametricPointHandle,
        end: SketchParametricPointHandle,
        targetLength: number,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'arc-length',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                center.xId, center.yId,
                start.xId, start.yId,
                end.xId, end.yId,
            ],
            branchSign,
            evaluate: (read) => {
                const startDx = read(start.xId) - read(center.xId);
                const startDy = read(start.yId) - read(center.yId);
                const endDx = read(end.xId) - read(center.xId);
                const endDy = read(end.yId) - read(center.yId);
                const startRadius = Math.hypot(startDx, startDy);
                const endRadius = Math.hypot(endDx, endDy);

                if (startRadius < SketchParametricEquationFactory.DEGENERATE_LENGTH
                    || endRadius < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`弧端点与圆心重合：${equationId}`);
                }

                const startAngle = Math.atan2(startDy, startDx);
                const endAngle = Math.atan2(endDy, endDx);
                let sweep = branchSign === 1 ? endAngle - startAngle : startAngle - endAngle;

                sweep %= Math.PI * 2;
                if (sweep <= 0) {
                    sweep += Math.PI * 2;
                }

                return [((startRadius + endRadius) / 2) * sweep - targetLength];
            },
        };
    }

    /**
     * 路径总长（尺寸：path-length，阶段 8）：链各段长度之和。
     *
     * 说明：链的端到端重合由命令层校验；方程只约束 Σ|段| = 目标值，
     * 段与段之间的连接关系由重合几何关系维持。段类型：
     * - line：两端点直线长；
     * - arc：半径参数 × 扫角（扫角由起/终点相对圆心的方位角按走向规约反算，
     *   随端点/圆心位置连续变化——弧长因此可参与驱动求解）；
     * - circle：`2π × 半径参数`；
     * - spline：初始采样弧长 × 整体缩放系数（与样条总长尺寸同一契约）。
     *
     * @param equationId 方程 ID。
     * @param segments 链上各段（按链序）。
     * @param targetLength 目标总长（米）。
     * @returns 残差方程。
     * @throws 当路径段列表为空时抛出错误。
     */
    public static pathLength(
        equationId: string,
        segments: readonly SketchParametricPathSegment[],
        targetLength: number,
    ): SketchParametricEquation {
        if (segments.length === 0) {
            throw new Error(`路径总长方程至少需要一段：${equationId}`);
        }

        const parameterIds = segments.flatMap((segment) => (
            SketchParametricEquationFactory.resolvePathSegmentParameterIds(segment)
        ));

        return {
            equationId,
            kind: 'path-length',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds,
            branchSign: null,
            evaluate: (read) => {
                const total = segments.reduce((sum, segment) => (
                    sum + SketchParametricEquationFactory.measurePathSegmentLength(read, segment)
                ), 0);

                return [total - targetLength];
            },
        };
    }

    /**
     * 创建操作点到固定目标点的瞬态偏好方程。
     *
     * 除普通重合残差外，该方程声明严格候选求解 seed：先把操作点参数预置为固定目标，
     * 让硬约束从鼠标位置调整其余自由度。候选不可达时求解器会恢复并求最近可行位置。
     *
     * @param equationId 方程 ID。
     * @param point 被操作点。
     * @param fixedTarget 固定鼠标目标点。
     * @returns 带参数 seed 绑定的偏好方程。
     */
    public static pointTargetPreference(
        equationId: string,
        point: SketchParametricPointHandle,
        fixedTarget: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            ...SketchParametricEquationFactory.coincident(
                equationId,
                point,
                fixedTarget,
                'preference',
            ),
            kind: 'operation-point-target',
            preferenceSeedBindings: [
                {targetParameterId: point.xId, sourceParameterId: fixedTarget.xId},
                {targetParameterId: point.yId, sourceParameterId: fixedTarget.yId},
            ],
        };
    }

    /**
     * 创建“目标点 = 源点绕中心点旋转固定角度”的二维刚性方程。
     *
     * @param equationId 方程 ID。
     * @param sourcePoint 种子实体上的源点。
     * @param targetPoint 派生实体上的目标点。
     * @param centerPoint 圆周阵列中心点。
     * @param rotationRad 目标实例相对种子实例的旋转角，单位弧度。
     * @returns 具有解析 Jacobian 的两行长度残差方程。
     */
    public static rotatedPointAroundCenter(
        equationId: string,
        sourcePoint: SketchParametricPointHandle,
        targetPoint: SketchParametricPointHandle,
        centerPoint: SketchParametricPointHandle,
        rotationRad: number,
    ): SketchParametricEquation {
        const cosine = Math.cos(rotationRad);
        const sine = Math.sin(rotationRad);
        /**
         * 同一个草图点可能同时承担阵列中心和种子定义点。参数系统要求每条方程的参数列唯一，
         * 因此必须按参数 ID 合并解析导数；保留重复列会让 Jacobian 列与参数向量错位，导致
         * 合法的“种子端点位于阵列中心”场景无法收敛。
         */
        const derivativeEntries = [
            [targetPoint.xId, 1, 0],
            [targetPoint.yId, 0, 1],
            [centerPoint.xId, cosine - 1, sine],
            [centerPoint.yId, -sine, cosine - 1],
            [sourcePoint.xId, -cosine, -sine],
            [sourcePoint.yId, sine, -cosine],
        ] as const;
        const derivativesByParameterId = new Map<string, [number, number]>();
        derivativeEntries.forEach(([parameterId, firstResidualDerivative, secondResidualDerivative]) => {
            const accumulatedDerivatives = derivativesByParameterId.get(parameterId) ?? [0, 0];
            accumulatedDerivatives[0] += firstResidualDerivative;
            accumulatedDerivatives[1] += secondResidualDerivative;
            derivativesByParameterId.set(parameterId, accumulatedDerivatives);
        });
        const parameterIds = [...derivativesByParameterId.keys()];
        return {
            equationId,
            kind: 'relation-circular-pattern-point',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds,
            branchSign: null,
            evaluateJacobian: () => [
                parameterIds.map((parameterId) => derivativesByParameterId.get(parameterId)![0]),
                parameterIds.map((parameterId) => derivativesByParameterId.get(parameterId)![1]),
            ],
            evaluate: (read) => {
                const sourceOffsetX = read(sourcePoint.xId) - read(centerPoint.xId);
                const sourceOffsetY = read(sourcePoint.yId) - read(centerPoint.yId);
                const expectedX = read(centerPoint.xId)
                    + cosine * sourceOffsetX
                    - sine * sourceOffsetY;
                const expectedY = read(centerPoint.yId)
                    + sine * sourceOffsetX
                    + cosine * sourceOffsetY;
                return [
                    read(targetPoint.xId) - expectedX,
                    read(targetPoint.yId) - expectedY,
                ];
            },
        };
    }

    /**
     * 创建“不旋转实例姿态”的圆周阵列点平移方程。
     *
     * 种子组全部唯一定义点的质心作为组锚点。实例锚点绕阵列中心旋转固定角度，
     * 但每个实例点只应用相同平移量，不旋转自身相对组锚点的方向，从而保持
     * `PatternRotate=false` 的原始姿态语义。
     *
     * @param equationId 方程 ID。
     * @param sourcePoint 种子组中的源点。
     * @param targetPoint 派生实例中的对应点。
     * @param centerPoint 圆周阵列中心点。
     * @param sourceGroupPoints 种子组全部唯一参数点。
     * @param rotationRad 当前实例相对种子实例的分布角。
     * @returns 两行长度残差方程。
     */
    public static translatedPointByRotatedGroupAnchor(
        equationId: string,
        sourcePoint: SketchParametricPointHandle,
        targetPoint: SketchParametricPointHandle,
        centerPoint: SketchParametricPointHandle,
        sourceGroupPoints: readonly SketchParametricPointHandle[],
        rotationRad: number,
    ): SketchParametricEquation {
        if (sourceGroupPoints.length === 0) {
            throw new Error('圆周阵列平移方程缺少种子组参数点。');
        }
        const cosine = Math.cos(rotationRad);
        const sine = Math.sin(rotationRad);
        const parameterIds = [...new Set([
            targetPoint.xId,
            targetPoint.yId,
            sourcePoint.xId,
            sourcePoint.yId,
            centerPoint.xId,
            centerPoint.yId,
            ...sourceGroupPoints.flatMap((point) => [point.xId, point.yId]),
        ])];
        return {
            equationId,
            kind: 'relation-circular-pattern-translate-point',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds,
            branchSign: null,
            evaluate: (read) => {
                let anchorX = 0;
                let anchorY = 0;
                sourceGroupPoints.forEach((point) => {
                    anchorX += read(point.xId);
                    anchorY += read(point.yId);
                });
                anchorX /= sourceGroupPoints.length;
                anchorY /= sourceGroupPoints.length;
                const anchorOffsetX = anchorX - read(centerPoint.xId);
                const anchorOffsetY = anchorY - read(centerPoint.yId);
                const targetAnchorX = read(centerPoint.xId)
                    + cosine * anchorOffsetX
                    - sine * anchorOffsetY;
                const targetAnchorY = read(centerPoint.yId)
                    + sine * anchorOffsetX
                    + cosine * anchorOffsetY;
                return [
                    read(targetPoint.xId) - read(sourcePoint.xId) - (targetAnchorX - anchorX),
                    read(targetPoint.yId) - read(sourcePoint.yId) - (targetAnchorY - anchorY),
                ];
            },
        };
    }

    /**
     * 点位于由中心、长轴正向点和短轴正向点定义的完整基础椭圆上。
     *
     * 椭圆局部坐标采用两条轴向量归一化投影：`u² + v² - 1 = 0`。
     * 长短轴正交由实体结构方程单独保证；任一轴退化时显式抛错并归类 invalid。
     *
     * @param equationId 方程 ID。
     * @param point 待约束点。
     * @param center 椭圆中心。
     * @param majorPositive 长轴正向控制点。
     * @param minorPositive 短轴正向控制点。
     * @returns 残差方程。
     */
    public static pointOnEllipse(
        equationId: string,
        point: SketchParametricPointHandle,
        center: SketchParametricPointHandle,
        majorPositive: SketchParametricPointHandle,
        minorPositive: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-on-ellipse',
            residualCount: 1,
            residualUnits: ['dimensionless'],
            parameterIds: [
                point.xId, point.yId,
                center.xId, center.yId,
                majorPositive.xId, majorPositive.yId,
                minorPositive.xId, minorPositive.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const majorX = read(majorPositive.xId) - read(center.xId);
                const majorY = read(majorPositive.yId) - read(center.yId);
                const minorX = read(minorPositive.xId) - read(center.xId);
                const minorY = read(minorPositive.yId) - read(center.yId);
                const majorLengthSquared = majorX * majorX + majorY * majorY;
                const minorLengthSquared = minorX * minorX + minorY * minorY;

                if (majorLengthSquared < SketchParametricEquationFactory.DEGENERATE_LENGTH ** 2
                    || minorLengthSquared < SketchParametricEquationFactory.DEGENERATE_LENGTH ** 2) {
                    throw new Error(`椭圆轴退化：${equationId}`);
                }

                const pointX = read(point.xId) - read(center.xId);
                const pointY = read(point.yId) - read(center.yId);
                const majorCoordinate = (pointX * majorX + pointY * majorY) / majorLengthSquared;
                const minorCoordinate = (pointX * minorX + pointY * minorY) / minorLengthSquared;

                return [majorCoordinate * majorCoordinate + minorCoordinate * minorCoordinate - 1];
            },
        };
    }

    /**
     * 点位于焦点—顶点定义的完整基础抛物线上。
     *
     * 以顶点为原点、顶点指向焦点为轴向，标准式为 `axis = side² / (4f)`。
     * 焦点与顶点重合时显式抛错并归类 invalid。
     *
     * @param equationId 方程 ID。
     * @param point 待约束点。
     * @param focal 抛物线焦点。
     * @param apex 抛物线顶点。
     * @returns 残差方程。
     */
    public static pointOnParabola(
        equationId: string,
        point: SketchParametricPointHandle,
        focal: SketchParametricPointHandle,
        apex: SketchParametricPointHandle,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-on-parabola',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                point.xId, point.yId,
                focal.xId, focal.yId,
                apex.xId, apex.yId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const axisX = read(focal.xId) - read(apex.xId);
                const axisY = read(focal.yId) - read(apex.yId);
                const focalLength = Math.hypot(axisX, axisY);

                if (focalLength < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`抛物线焦点与顶点重合：${equationId}`);
                }

                const unitAxisX = axisX / focalLength;
                const unitAxisY = axisY / focalLength;
                const offsetX = read(point.xId) - read(apex.xId);
                const offsetY = read(point.yId) - read(apex.yId);
                const axisOffset = offsetX * unitAxisX + offsetY * unitAxisY;
                const sideOffset = offsetX * -unitAxisY + offsetY * unitAxisX;

                return [axisOffset - sideOffset * sideOffset / (4 * focalLength)];
            },
        };
    }

    /**
     * 点位于抛物线指定长度参数对应的精确位置。
     *
     * 以顶点为原点、顶点指向焦点为轴向，侧向单位向量取 `axis × planeNormal`。
     * 给定侧向长度参数 `t` 后，标准式位置为
     * `apex + side * t + axis * t² / (4f)`；两行长度残差同时锁定曲线分支和端点位置。
     *
     * @param equationId 方程 ID。
     * @param point 可见段端点。
     * @param focal 抛物线焦点。
     * @param apex 抛物线顶点。
     * @param curveParameterId 端点侧向长度参数 ID。
     * @returns 两行坐标残差方程。
     */
    public static pointAtParabolaParameter(
        equationId: string,
        point: SketchParametricPointHandle,
        focal: SketchParametricPointHandle,
        apex: SketchParametricPointHandle,
        curveParameterId: string,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'point-at-parabola-parameter',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [
                point.xId, point.yId,
                focal.xId, focal.yId,
                apex.xId, apex.yId,
                curveParameterId,
            ],
            branchSign: null,
            evaluate: (read) => {
                const axisX = read(focal.xId) - read(apex.xId);
                const axisY = read(focal.yId) - read(apex.yId);
                const focalLength = Math.hypot(axisX, axisY);

                if (focalLength < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`抛物线焦点与顶点重合：${equationId}`);
                }

                const unitAxisX = axisX / focalLength;
                const unitAxisY = axisY / focalLength;
                const unitSideX = unitAxisY;
                const unitSideY = -unitAxisX;
                const curveParameter = read(curveParameterId);
                const axisOffset = curveParameter * curveParameter / (4 * focalLength);
                const expectedX = read(apex.xId)
                    + unitSideX * curveParameter
                    + unitAxisX * axisOffset;
                const expectedY = read(apex.yId)
                    + unitSideY * curveParameter
                    + unitAxisY * axisOffset;

                return [
                    read(point.xId) - expectedX,
                    read(point.yId) - expectedY,
                ];
            },
        };
    }

    /**
     * 无限直线与椭圆相切。
     *
     * 对单位法向 `n`，椭圆在该方向的支撑半径为
     * `sqrt((n·majorVector)² + (n·minorVector)²)`；圆心到直线的有符号距离等于
     * 分支符号乘支撑半径即为相切条件。
     *
     * @param equationId 方程 ID。
     * @param center 椭圆中心。
     * @param majorPositive 长轴正向控制点。
     * @param minorPositive 短轴正向控制点。
     * @param lineStart 直线起点。
     * @param lineEnd 直线终点。
     * @param branchSign 椭圆位于直线哪一侧。
     * @returns 残差方程。
     */
    public static lineEllipseTangent(
        equationId: string,
        center: SketchParametricPointHandle,
        majorPositive: SketchParametricPointHandle,
        minorPositive: SketchParametricPointHandle,
        lineStart: SketchParametricPointHandle,
        lineEnd: SketchParametricPointHandle,
        branchSign: 1 | -1,
    ): SketchParametricEquation {
        return {
            equationId,
            kind: 'line-ellipse-tangent',
            residualCount: 1,
            residualUnits: ['length'],
            parameterIds: [
                center.xId, center.yId,
                majorPositive.xId, majorPositive.yId,
                minorPositive.xId, minorPositive.yId,
                lineStart.xId, lineStart.yId,
                lineEnd.xId, lineEnd.yId,
            ],
            branchSign,
            evaluate: (read) => {
                const direction = SketchParametricEquationFactory.readUnitDirection(read, lineStart, lineEnd);
                const normalX = -direction.y;
                const normalY = direction.x;
                const centerOffsetX = read(center.xId) - read(lineStart.xId);
                const centerOffsetY = read(center.yId) - read(lineStart.yId);
                const signedDistance = normalX * centerOffsetX + normalY * centerOffsetY;
                const majorX = read(majorPositive.xId) - read(center.xId);
                const majorY = read(majorPositive.yId) - read(center.yId);
                const minorX = read(minorPositive.xId) - read(center.xId);
                const minorY = read(minorPositive.yId) - read(center.yId);
                const supportMajor = normalX * majorX + normalY * majorY;
                const supportMinor = normalX * minorX + normalY * minorY;
                const supportRadius = Math.hypot(supportMajor, supportMinor);

                if (supportRadius < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`椭圆支撑半径退化：${equationId}`);
                }

                return [signedDistance - branchSign * supportRadius];
            },
        };
    }

    /**
     * 两条圆系/椭圆隐式曲线在同一接触点相切。
     *
     * 三个残差分别表示：接触点位于第一曲线、接触点位于第二曲线、两曲线在接触点
     * 的隐式法向平行。接触点自身提供两个瞬态自由参数，因此整组净增加一条相切约束。
     *
     * @param equationId 方程 ID。
     * @param contact 接触点瞬态参数句柄。
     * @param first 第一条隐式曲线。
     * @param second 第二条隐式曲线。
     * @returns 三行残差方程。
     */
    public static implicitCurvePairTangent(
        equationId: string,
        contact: SketchParametricPointHandle,
        first: SketchParametricImplicitCurveDescriptor,
        second: SketchParametricImplicitCurveDescriptor,
    ): SketchParametricEquation {
        const parameterIds = new Set<string>([contact.xId, contact.yId]);

        SketchParametricEquationFactory.resolveImplicitCurveParameterIds(first)
            .forEach((parameterId) => parameterIds.add(parameterId));
        SketchParametricEquationFactory.resolveImplicitCurveParameterIds(second)
            .forEach((parameterId) => parameterIds.add(parameterId));

        return {
            equationId,
            kind: 'implicit-curve-pair-tangent',
            residualCount: 3,
            residualUnits: ['dimensionless', 'dimensionless', 'direction'],
            parameterIds: [...parameterIds],
            branchSign: null,
            evaluate: (read) => {
                const firstState = SketchParametricEquationFactory.evaluateImplicitCurve(read, first, contact);
                const secondState = SketchParametricEquationFactory.evaluateImplicitCurve(read, second, contact);
                const firstNormalLength = Math.hypot(firstState.gradientX, firstState.gradientY);
                const secondNormalLength = Math.hypot(secondState.gradientX, secondState.gradientY);

                if (firstNormalLength < SketchParametricEquationFactory.DEGENERATE_LENGTH
                    || secondNormalLength < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`相切接触点法向退化：${equationId}`);
                }

                const normalizedCross = (
                    firstState.gradientX * secondState.gradientY
                    - firstState.gradientY * secondState.gradientX
                ) / (firstNormalLength * secondNormalLength);

                return [firstState.value, secondState.value, normalizedCross];
            },
        };
    }

    /**
     * 点位于真实参数样条曲线上。
     *
     * @param equationId 方程 ID。
     * @param point 待约束点。
     * @param curveParameterId 曲线参数 ID。
     * @param spline 样条精确求值描述符。
     * @returns x/y 两行重合残差。
     */
    public static pointOnSpline(
        equationId: string,
        point: SketchParametricPointHandle,
        curveParameterId: string,
        spline: SketchParametricSplineCurveDescriptor,
    ): SketchParametricEquation {
        const evaluator = new SketchParametricSplineEvaluator();
        const splinePoint: SketchParametricSplinePointEvaluation = {x: 0, y: 0};
        const parameterIds = new Set<string>([point.xId, point.yId, curveParameterId]);

        spline.controlPoints.forEach((handle) => {
            parameterIds.add(handle.xId);
            parameterIds.add(handle.yId);
        });

        return {
            equationId,
            kind: 'point-on-spline',
            residualCount: 2,
            residualUnits: ['length', 'length'],
            parameterIds: [...parameterIds],
            branchSign: null,
            evaluate: (read) => {
                evaluator.evaluatePoint(read, spline, read(curveParameterId), splinePoint);

                return [
                    read(point.xId) - splinePoint.x,
                    read(point.yId) - splinePoint.y,
                ];
            },
        };
    }

    /**
     * 样条与圆系/椭圆在样条参数点相切。
     *
     * 第一行保证样条参数点位于目标隐式曲线上，第二行保证样条一阶导数与目标
     * 隐式法向垂直。曲线参数提供一个瞬态自由度，因此整组净增加一条相切约束。
     *
     * @param equationId 方程 ID。
     * @param curveParameterId 样条接触参数 ID。
     * @param spline 样条精确求值描述符。
     * @param curve 目标圆系或椭圆隐式曲线。
     * @returns 两行残差方程。
     */
    public static splineImplicitCurveTangent(
        equationId: string,
        curveParameterId: string,
        spline: SketchParametricSplineCurveDescriptor,
        curve: SketchParametricImplicitCurveDescriptor,
    ): SketchParametricEquation {
        const evaluator = new SketchParametricSplineEvaluator();
        const splineState: SketchParametricSplineDifferentialEvaluation = {
            x: 0,
            y: 0,
            tangentX: 0,
            tangentY: 0,
        };
        const parameterIds = new Set<string>([curveParameterId]);

        spline.controlPoints.forEach((handle) => {
            parameterIds.add(handle.xId);
            parameterIds.add(handle.yId);
        });
        SketchParametricEquationFactory.resolveImplicitCurveParameterIds(curve)
            .forEach((parameterId) => parameterIds.add(parameterId));

        return {
            equationId,
            kind: 'spline-implicit-curve-tangent',
            residualCount: 2,
            residualUnits: ['dimensionless', 'direction'],
            parameterIds: [...parameterIds],
            branchSign: null,
            evaluate: (read) => {
                evaluator.evaluateDifferential(read, spline, read(curveParameterId), splineState);
                const implicitState = SketchParametricEquationFactory.evaluateImplicitCurveAtCoordinates(
                    read,
                    curve,
                    splineState.x,
                    splineState.y,
                );
                const tangentLength = Math.hypot(splineState.tangentX, splineState.tangentY);
                const normalLength = Math.hypot(implicitState.gradientX, implicitState.gradientY);

                if (tangentLength < SketchParametricEquationFactory.DEGENERATE_LENGTH
                    || normalLength < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                    throw new Error(`样条相切导数或目标法向退化：${equationId}`);
                }

                const normalizedDot = (
                    splineState.tangentX * implicitState.gradientX
                    + splineState.tangentY * implicitState.gradientY
                ) / (tangentLength * normalLength);

                return [implicitState.value, normalizedDot];
            },
        };
    }

    /**
     * 解析隐式曲线涉及的几何参数 ID。
     *
     * @param curve 隐式曲线描述符。
     * @returns 参数 ID 列表。
     */
    private static resolveImplicitCurveParameterIds(
        curve: SketchParametricImplicitCurveDescriptor,
    ): readonly string[] {
        if (curve.kind === 'circle') {
            return [curve.center.xId, curve.center.yId, curve.radiusParameterId];
        }

        return [
            curve.center.xId, curve.center.yId,
            curve.majorPositive.xId, curve.majorPositive.yId,
            curve.minorPositive.xId, curve.minorPositive.yId,
        ];
    }

    /**
     * 计算接触点处的隐式函数值与梯度。
     *
     * @param read 参数读取函数。
     * @param curve 隐式曲线描述符。
     * @param point 接触点。
     * @returns 归一化隐式函数值及二维梯度。
     */
    private static evaluateImplicitCurve(
        read: (parameterId: string) => number,
        curve: SketchParametricImplicitCurveDescriptor,
        point: SketchParametricPointHandle,
    ): SketchParametricImplicitCurveEvaluation {
        return SketchParametricEquationFactory.evaluateImplicitCurveAtCoordinates(
            read,
            curve,
            read(point.xId),
            read(point.yId),
        );
    }

    /**
     * 计算给定二维坐标处的隐式函数值与梯度。
     *
     * @param read 参数读取函数。
     * @param curve 隐式曲线描述符。
     * @param x 二维点 x 坐标。
     * @param y 二维点 y 坐标。
     * @returns 归一化隐式函数值及二维梯度。
     */
    private static evaluateImplicitCurveAtCoordinates(
        read: (parameterId: string) => number,
        curve: SketchParametricImplicitCurveDescriptor,
        x: number,
        y: number,
    ): SketchParametricImplicitCurveEvaluation {
        const pointX = x - read(curve.center.xId);
        const pointY = y - read(curve.center.yId);

        if (curve.kind === 'circle') {
            const radius = read(curve.radiusParameterId);

            if (radius < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
                throw new Error(`隐式圆半径退化：${curve.radiusParameterId}`);
            }

            const radiusSquared = radius * radius;

            return {
                value: (pointX * pointX + pointY * pointY) / radiusSquared - 1,
                gradientX: 2 * pointX / radiusSquared,
                gradientY: 2 * pointY / radiusSquared,
            };
        }

        const majorX = read(curve.majorPositive.xId) - read(curve.center.xId);
        const majorY = read(curve.majorPositive.yId) - read(curve.center.yId);
        const minorX = read(curve.minorPositive.xId) - read(curve.center.xId);
        const minorY = read(curve.minorPositive.yId) - read(curve.center.yId);
        const majorLengthSquared = majorX * majorX + majorY * majorY;
        const minorLengthSquared = minorX * minorX + minorY * minorY;

        if (majorLengthSquared < SketchParametricEquationFactory.DEGENERATE_LENGTH ** 2
            || minorLengthSquared < SketchParametricEquationFactory.DEGENERATE_LENGTH ** 2) {
            throw new Error(`隐式椭圆轴退化：${curve.center.xId}/${curve.center.yId}`);
        }

        const majorCoordinate = (pointX * majorX + pointY * majorY) / majorLengthSquared;
        const minorCoordinate = (pointX * minorX + pointY * minorY) / minorLengthSquared;

        return {
            value: majorCoordinate * majorCoordinate + minorCoordinate * minorCoordinate - 1,
            gradientX: 2 * majorCoordinate * majorX / majorLengthSquared
                + 2 * minorCoordinate * minorX / minorLengthSquared,
            gradientY: 2 * majorCoordinate * majorY / majorLengthSquared
                + 2 * minorCoordinate * minorY / minorLengthSquared,
        };
    }

    /**
     * 解析单条曲线长度描述符依赖的参数 ID。
     *
     * @param segment 曲线长度描述符。
     * @returns 参数 ID 列表。
     */
    private static resolvePathSegmentParameterIds(
        segment: SketchParametricPathSegment,
    ): readonly string[] {
        if (segment.kind === 'line') {
            return [segment.start.xId, segment.start.yId, segment.end.xId, segment.end.yId];
        }
        if (segment.kind === 'arc') {
            return [
                segment.start.xId, segment.start.yId,
                segment.end.xId, segment.end.yId,
                segment.center.xId, segment.center.yId,
                segment.radiusParameterId,
            ];
        }
        if (segment.kind === 'circle') {
            return [segment.radiusParameterId];
        }

        return [segment.scaleParameterId];
    }

    /**
     * 测量单条已参数化曲线的当前真实长度。
     *
     * @param read 参数读取函数。
     * @param segment 曲线长度描述符。
     * @returns 当前曲线长度（米）。
     */
    private static measurePathSegmentLength(
        read: (parameterId: string) => number,
        segment: SketchParametricPathSegment,
    ): number {
        if (segment.kind === 'line') {
            const deltaX = read(segment.end.xId) - read(segment.start.xId);
            const deltaY = read(segment.end.yId) - read(segment.start.yId);

            return Math.hypot(deltaX, deltaY);
        }
        if (segment.kind === 'arc') {
            const centerX = read(segment.center.xId);
            const centerY = read(segment.center.yId);
            const startAngle = Math.atan2(
                read(segment.start.yId) - centerY,
                read(segment.start.xId) - centerX,
            );
            const endAngle = Math.atan2(
                read(segment.end.yId) - centerY,
                read(segment.end.xId) - centerX,
            );
            let sweep = segment.clockwise ? startAngle - endAngle : endAngle - startAngle;

            // 走向规约与真弧长测量一致：扫角收进 (0, 2π]。
            sweep %= Math.PI * 2;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }

            return read(segment.radiusParameterId) * sweep;
        }
        if (segment.kind === 'circle') {
            return Math.PI * 2 * read(segment.radiusParameterId);
        }

        return segment.baseLength * read(segment.scaleParameterId);
    }

    /**
     * 读取两点定义的单位方向；零长度抛错（invalid 语义来源）。
     *
     * @param read 参数读取函数。
     * @param start 起点。
     * @param end 终点。
     * @returns 单位方向向量。
     */
    private static readUnitDirection(
        read: (parameterId: string) => number,
        start: SketchParametricPointHandle,
        end: SketchParametricPointHandle,
    ): {x: number; y: number} {
        const dx = read(end.xId) - read(start.xId);
        const dy = read(end.yId) - read(start.yId);
        const length = Math.hypot(dx, dy);

        if (length < SketchParametricEquationFactory.DEGENERATE_LENGTH) {
            throw new Error(`零长度方向：${start.xId}/${start.yId} -> ${end.xId}/${end.yId}`);
        }

        return {x: dx / length, y: dy / length};
    }

    /**
     * 把角度差规约到 (-π, π]，保证角度残差连续可微分（远离 ±π 边界时）。
     *
     * @param angle 输入角度（弧度）。
     * @returns 规约后角度。
     */
    public static wrapToPi(angle: number): number {
        let wrapped = angle;

        while (wrapped > Math.PI) {
            wrapped -= 2 * Math.PI;
        }
        while (wrapped <= -Math.PI) {
            wrapped += 2 * Math.PI;
        }

        return wrapped;
    }
}
